import React, { useState, useEffect, useRef } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { Form, InputGroup, FormControlProps } from 'react-bootstrap';
import { BiSearch } from 'react-icons/bi';
import { DEFAULT_ADDRESS } from '@constants/jobInitialSearch';
import { isBrowserOutdated } from '@utils/BrowserUtils';

interface MapsAutoCompleteProps extends FormControlProps {
  defaultAddress?: string;
  setSelectedPlace?: (place: google.maps.places.PlaceResult | null) => void;
  setInputValue?: (value: string) => void;
}

const countryRestriction = ['US', 'CA'];

function MapsAutoComplete({
  defaultAddress,
  setInputValue,
  setSelectedPlace,
  ...props
}: MapsAutoCompleteProps) {
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary('places');

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      componentRestrictions: { country: countryRestriction },
      fields: [
        'address_components',
        'formatted_address',
        'geometry',
        'icon',
        'name',
      ],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    if (isBrowserOutdated()) {
      console.warn('Safari version is outdated');
      return;
    }

    const listener = placeAutocomplete.addListener('place_changed', () => {
      setSelectedPlace?.(placeAutocomplete.getPlace());
    });

    // eslint-disable-next-line consistent-return
    return () => {
      listener.remove();
    };
  }, [setSelectedPlace, placeAutocomplete]);

  return (
    <div className="autocomplete-container">
      <div className="autocomplete-wrapper">
        <InputGroup>
          <InputGroup.Text className="search-input">
            <BiSearch size={22} />
          </InputGroup.Text>
          <Form.Control
            className="search-input"
            ref={inputRef}
            {...props}
            defaultValue={defaultAddress || DEFAULT_ADDRESS || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setInputValue?.(e.target.value);
            }}
          />
        </InputGroup>
      </div>
    </div>
  );
}

export default MapsAutoComplete;
